import { useState } from "react";
import { PlusIcon } from "vega-icons";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import {
  UiCapabilityKey,
  useUiCapabilities,
} from "@vega-api/extended/auth/useUiCapabilities";
import { type CollaboratorDetails } from "@vega-api/generated/types/collaboratorDetails";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { CardVariant } from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { SettingsSectionLayout } from "@vega-modules/scenes/Settings/components/SettingsSectionLayout";
import { ResendInviteModal } from "@vega-modules/scenes/Settings/sections/UserManagement/modals/ResendInviteModal";

import { CollaboratorsSideDrawer } from "../components/CollaboratorsSideDrawer";
import { getCollaboratorRoleOptions } from "../components/collaboratorsV2Constants";
import { InviteCollaboratorsDialog } from "../components/InviteCollaboratorsDialog";
import { InviteCollaboratorsDialogV2 } from "../components/InviteCollaboratorsDialogV2";
import { ManageRolesModal } from "../components/ManageRolesModal/ManageRolesModal";
import { useCollaboratorRoles } from "../hooks/useCollaboratorRoles";
import {
  type CollaboratorResendUser,
  useCollaboratorsTableFilters,
} from "../hooks/useCollaboratorsTableFilters";

export function CollaboratorsPage() {
  const { capabilities } = useUiCapabilities();
  const [selectedCollaborator, setSelectedCollaborator] =
    useState<CollaboratorDetails | null>(null);
  const isSideDrawerOpen = useBoolean(false);
  const [isInviteCollaboratorsDialogOpen, setIsInviteCollaboratorsDialogOpen] =
    useState(false);
  const isManageRolesModalOpen = useBoolean(false);
  const { featureFlag } = useFeatureFlag();

  const resendInviteModalState = useBoolean(false);
  const [selectedResendUser, setSelectedResendUser] =
    useState<CollaboratorResendUser | null>(null);

  const handleResendInvite = (user: CollaboratorResendUser) => {
    setSelectedResendUser(user);
    resendInviteModalState.setTrue();
  };

  const {
    collaboratorsColumns,
    collaboratorsData = [],
    filteredCollaborators,
    isCollaboratorsLoading,
    RenderFilters,
    hasActiveFilters,
    paginationElement,
  } = useCollaboratorsTableFilters(handleResendInvite);
  const { roles: uniqueRoles } = useCollaboratorRoles();
  const inviteRolesOptions = getCollaboratorRoleOptions(uniqueRoles);

  const canManageCollaborators =
    capabilities[UiCapabilityKey.CanManageWorkbenchCollaborators];
  const isCollaboratorsV2 = featureFlag[FeatureFlag.GP_COLLABORATORS_V2];

  const manageRolesButton =
    isCollaboratorsV2 && canManageCollaborators ? (
      <ThemedButton
        dataTestId="bulk-manage-roles-button"
        onClick={isManageRolesModalOpen.setTrue}
        variant="secondary"
      >
        {m.settings_collaborators_manageRoles()}
      </ThemedButton>
    ) : null;

  const renderInviteCollaboratorButton = (dataTestId: string) =>
    featureFlag.gp_settings_invite_collaborators && canManageCollaborators ? (
      <ThemedButton
        dataTestId={dataTestId}
        icon={<PlusIcon />}
        iconPosition="right"
        onClick={() => setIsInviteCollaboratorsDialogOpen(true)}
        variant="primary"
      >
        {m.settings_collaborators_inviteCollaborator()}
      </ThemedButton>
    ) : null;

  const inviteCollaboratorButton = renderInviteCollaboratorButton(
    "invite-collaborator",
  );

  const headerActions =
    manageRolesButton || inviteCollaboratorButton ? (
      <div className="flex gap-2">
        {manageRolesButton}
        {inviteCollaboratorButton}
      </div>
    ) : null;

  const getEmptyCardState = () => {
    if (hasActiveFilters) {
      return {
        description: m.settings_collaborators_noCollaboratorsFoundDescription(),
        title: m.settings_collaborators_noCollaboratorsFoundTitle(),
        variant: CardVariant.SearchUser,
      };
    }

    return {
      description: m.settings_collaborators_noCollaboratorsResults(),
      variant: CardVariant.SearchUser,
      actions: renderInviteCollaboratorButton("invite-collaborator-empty"),
    };
  };

  const handleOpenSideDrawer = (collaborator: CollaboratorDetails) => {
    setSelectedCollaborator(collaborator);
    isSideDrawerOpen.setTrue();
  };

  return (
    <SettingsSectionLayout
      className="md:px-2"
      description={m.settings_collaborators_description()}
      headerAction={headerActions}
      title={m.settings_collaborators_title()}
    >
      <div className="flex flex-col gap-6">
        {collaboratorsData.length > 0 && RenderFilters}
        <CoreTable
          columns={collaboratorsColumns}
          data={filteredCollaborators}
          dataTestId="collaborators-table"
          emptyState={getEmptyCardState()}
          isLoading={isCollaboratorsLoading}
          onRowClick={handleOpenSideDrawer}
          paginationElement={paginationElement}
        />
      </div>
      <CollaboratorsSideDrawer
        isSideDrawerOpen={isSideDrawerOpen.value}
        selectedCollaborator={selectedCollaborator}
        setIsSideDrawerOpen={isSideDrawerOpen.setIsOpen}
        setSelectedCollaborator={setSelectedCollaborator}
      />
      {isCollaboratorsV2 ? (
        <InviteCollaboratorsDialogV2
          isOpen={isInviteCollaboratorsDialogOpen}
          roles={uniqueRoles}
          setOpen={setIsInviteCollaboratorsDialogOpen}
        />
      ) : (
        <InviteCollaboratorsDialog
          isOpen={isInviteCollaboratorsDialogOpen}
          rolesOptions={inviteRolesOptions}
          setOpen={setIsInviteCollaboratorsDialogOpen}
        />
      )}
      {isCollaboratorsV2 && canManageCollaborators ? (
        <ManageRolesModal
          open={isManageRolesModalOpen.value}
          setOpen={isManageRolesModalOpen.setIsOpen}
        />
      ) : null}
      {selectedResendUser ? (
        <ResendInviteModal
          invitationVariant="workbench-workbench"
          onClose={resendInviteModalState.setFalse}
          open={resendInviteModalState.value}
          showDescription={false}
          user={selectedResendUser}
        />
      ) : null}
    </SettingsSectionLayout>
  );
}
